/**
 * The "Scheduled replicas" band on a dedicated Baseten model's page
 * (TRA-1426), under "Replicas over time": the windows during which the
 * model keeps a replica range of its own, with add, edit, on/off and delete.
 *
 * The windows live on the Baseten environment the model serves from and are
 * read live through the gateway (refetched every 60s). A save sends the full
 * list of weekday windows with the one change applied, which is what the api
 * takes (scheduleWindows.ts). An hourly or one-time window set on Baseten is
 * listed and left alone.
 *
 * `ScheduleTable` is the band's table without its queries, so its rows are
 * testable from props.
 *
 * Under the list, `WindowUsageBlock` says how much of each enabled weekday
 * window the traffic used over the last 7 days (TRA-1437): the floor, the
 * mean replicas running, the mean percent of them used, and how long every
 * running replica was full. For choosing a floor with.
 *
 * Under that, `RecentChangesList` is the last changes of the model's replica
 * settings (TRA-1558): when, who (or "changed in Baseten"), and what
 * changed, newest first. Read-only for everyone who can see the page.
 */

import { useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import type {
  ModelScalingUsageResponse,
  ModelScheduleWindow,
  ModelSchedulesResponse,
  ReplicaSettingChangeResponse,
  UnwrappedQuery,
} from '@/hooks/useModels'
import {
  RECENT_CHANGES_LIMIT,
  isScheduleConflict,
  useModelScalingChanges,
  useModelScalingUsage,
  useModelSchedules,
  useUpdateModelSchedules,
} from '@/hooks/useModels'
import { useTeam } from '@/contexts/TeamContext'
import { ModelHomeSection } from '@/components/models/home/ModelHomeSection'
import { windowUsageRows } from '@/components/models/home/replicaUsage'
import { ScheduleWindowDialog } from '@/components/models/home/ScheduleWindowDialog'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

import {
  applyChange,
  baseFor,
  browserTimezone,
  cadenceLabel,
  changeAuthor,
  editableWindows,
  formatChangeTime,
  formatDays,
  formatHours,
  formatReplicas,
  MAX_WINDOWS,
  toWindowUpdate,
  type ExistingWindow,
  type ScheduleDialog,
  type ScheduleSnapshot,
  type WindowChange,
} from './scheduleWindows'

interface ScheduleTableProps {
  schedules: ModelSchedulesResponse
  /** False for a restricted team, and while a window cannot be read. */
  canEdit: boolean
  isPending: boolean
  onToggle: (entry: ExistingWindow, enabled: boolean) => void
  onEdit: (entry: ExistingWindow) => void
  onRemove: (entry: ExistingWindow) => void
}

function WindowActions({
  editable,
  isPending,
  onEdit,
  onRemove,
}: {
  /** Null for a window of another cadence, which this page does not edit. */
  editable: ExistingWindow | null
  isPending: boolean
  onEdit: (entry: ExistingWindow) => void
  onRemove: (entry: ExistingWindow) => void
}): React.JSX.Element {
  if (editable === null) {
    return <span className="text-xs text-muted-foreground">Edit on Baseten</span>
  }
  return (
    <div className="flex justify-end gap-1">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Edit ${editable.name}`}
        disabled={isPending}
        onClick={() => { onEdit(editable) }}
      >
        <Pencil />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Delete ${editable.name}`}
        disabled={isPending}
        onClick={() => { onRemove(editable) }}
      >
        <Trash2 />
      </Button>
    </div>
  )
}

function WindowRow({
  entry,
  active,
  canEdit,
  isPending,
  onToggle,
  onEdit,
  onRemove,
}: Omit<ScheduleTableProps, 'schedules'> & {
  entry: ModelScheduleWindow
  active: boolean
}): React.JSX.Element {
  const editable = toWindowUpdate(entry)
  const { min_replica: min, max_replica: max } = entry
  return (
    <TableRow className={cn(!entry.enabled && 'text-muted-foreground')}>
      <TableCell className="text-sm">
        <span className="flex flex-wrap items-center gap-2">
          {entry.name}
          {active && <Badge variant="success">Active now</Badge>}
        </span>
      </TableCell>
      <TableCell className="text-sm">
        {editable === null ? cadenceLabel(entry.cadence) : formatDays(editable.weekdays)}
      </TableCell>
      <TableCell className="font-mono text-xs tabular-nums">
        {editable === null ? '—' : formatHours(editable)}
      </TableCell>
      <TableCell className="font-mono text-xs tabular-nums">
        {min === null || max === null ? '—' : formatReplicas(min, max)}
      </TableCell>
      <TableCell>
        {canEdit && editable !== null ? (
          <Switch
            checked={entry.enabled}
            disabled={isPending}
            // The switch reads its own on or off; the label says what it switches.
            aria-label={`${entry.name} window`}
            onCheckedChange={(enabled) => { onToggle(editable, enabled) }}
          />
        ) : (
          <span className="font-mono text-[11px] uppercase tracking-wider">
            {entry.enabled ? 'On' : 'Off'}
          </span>
        )}
      </TableCell>
      {canEdit && (
        <TableCell className="text-right">
          <WindowActions
            editable={editable}
            isPending={isPending}
            onEdit={onEdit}
            onRemove={onRemove}
          />
        </TableCell>
      )}
    </TableRow>
  )
}

export function ScheduleTable({
  schedules,
  canEdit,
  ...rowProps
}: ScheduleTableProps): React.JSX.Element {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Window</TableHead>
          <TableHead>Days</TableHead>
          <TableHead>
            Time
            {schedules.timezone !== null && (
              // An IANA name is data: it keeps its own case in an uppercase header.
              <span className="normal-case"> ({schedules.timezone})</span>
            )}
          </TableHead>
          <TableHead>Replicas</TableHead>
          <TableHead>State</TableHead>
          {canEdit && (
            <TableHead>
              <span className="sr-only">Actions</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {schedules.schedules.map((entry) => (
          <WindowRow
            key={entry.id ?? entry.name}
            entry={entry}
            active={entry.id !== null && entry.id === schedules.active_schedule_id}
            canEdit={canEdit}
            {...rowProps}
          />
        ))}
      </TableBody>
    </Table>
  )
}

/** The table reads a week, so every weekday of every window is in it. */
const USAGE_HOURS = 168

export function WindowUsageTable({
  usage,
  timezone,
}: {
  usage: ModelScalingUsageResponse
  timezone: string | null
}): React.JSX.Element | null {
  const rows = windowUsageRows(usage.windows)
  if (rows.length === 0) return null
  return (
    <div className="mt-6">
      <h3 className="text-sm font-medium">How much of each window you used (last 7 days)</h3>
      <Table className="mt-2">
        <TableHeader>
          <TableRow>
            <TableHead>Window</TableHead>
            <TableHead>Days</TableHead>
            <TableHead>
              Time
              {timezone !== null && <span className="normal-case"> ({timezone})</span>}
            </TableHead>
            <TableHead>Floor</TableHead>
            <TableHead>Avg running</TableHead>
            <TableHead>Avg % used</TableHead>
            <TableHead>Time full</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell className="text-sm">{row.name}</TableCell>
              <TableCell className="text-sm">{row.days}</TableCell>
              <TableCell className="font-mono text-xs tabular-nums">{row.hours}</TableCell>
              <TableCell className="font-mono text-xs tabular-nums">{row.floor}</TableCell>
              <TableCell className="font-mono text-xs tabular-nums">{row.avgRunning}</TableCell>
              <TableCell className="font-mono text-xs tabular-nums">{row.avgUsed}</TableCell>
              <TableCell className="font-mono text-xs tabular-nums">{row.timeFull}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="mt-2 text-xs text-muted-foreground">
        Full means every running replica was busy and requests had to wait. Uses the windows as
        they are set now.
      </p>
    </div>
  )
}

/** The usage table, or one muted line while the usage cannot be read. */
function WindowUsageBlock({
  usage,
  timezone,
}: {
  usage: UnwrappedQuery<ModelScalingUsageResponse>
  timezone: string | null
}): React.JSX.Element | null {
  if (usage.isError) {
    return <p className="mt-4 text-sm text-muted-foreground">Usage could not be loaded.</p>
  }
  if (usage.data === undefined) return null
  return <WindowUsageTable usage={usage.data} timezone={timezone} />
}

export function RecentChangesList({
  changes,
}: {
  changes: ReplicaSettingChangeResponse[]
}): React.JSX.Element {
  return (
    <div className="mt-6">
      <h3 className="text-sm font-medium">Recent changes</h3>
      {changes.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">No changes saved here yet.</p>
      ) : (
        <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
          {changes.map((change) => (
            <li key={change.id} className="flex flex-wrap gap-x-2">
              <span className="font-mono tabular-nums">{formatChangeTime(change.changed_at)}</span>
              <span>{changeAuthor(change)}</span>
              <span className="text-foreground">{change.summary}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** The list, or nothing while it cannot be read: the schedule above it still reads. */
function RecentChangesBlock({
  changes,
}: {
  changes: UnwrappedQuery<ReplicaSettingChangeResponse[]>
}): React.JSX.Element | null {
  if (changes.isError) {
    return <p className="mt-4 text-sm text-muted-foreground">Recent changes could not be loaded.</p>
  }
  if (changes.data === undefined) return null
  return <RecentChangesList changes={changes.data} />
}

const CLOSED: ScheduleDialog = { kind: 'closed' }

export function BasetenScheduleSection({
  modelId,
}: {
  modelId: string
}): React.JSX.Element {
  const { isRestricted } = useTeam()
  const schedules = useModelSchedules(modelId, true)
  const usage = useModelScalingUsage(modelId, true, USAGE_HOURS)
  const changes = useModelScalingChanges(modelId, true, RECENT_CHANGES_LIMIT)
  const mutation = useUpdateModelSchedules()
  const [dialog, setDialog] = useState<ScheduleDialog>(CLOSED)
  const data = schedules.data
  // Null while a weekday window cannot be read: a save would delete it.
  const windows = data === undefined ? null : editableWindows(data)
  const canEdit = !isRestricted && windows !== null
  const isFull = (data?.schedules.length ?? 0) >= MAX_WINDOWS
  // The schedule as this page holds it now: what a new window or a switch is built on.
  const current: ScheduleSnapshot | null =
    data === undefined || windows === null
      ? null
      : {
          windows,
          timezone: data.timezone ?? browserTimezone(),
          revision: data.revision,
        }

  function save(change: WindowChange, done: string): void {
    if (current === null) return
    mutation.mutate(
      { modelId, data: applyChange(baseFor(dialog, current), change) },
      {
        onSuccess: () => {
          setDialog(CLOSED)
          toast.success(done)
        },
        onError: (error) => {
          // The hook has said why and reloaded the list. An open dialog still
          // holds the window as it was, so it closes: the next edit starts
          // from the window as it is.
          if (isScheduleConflict(error)) setDialog(CLOSED)
        },
      },
    )
  }

  return (
    <ModelHomeSection
      title="Scheduled replicas"
      subtitle="Replica ranges that apply during set hours of the week"
      action={
        canEdit && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 rounded-sm px-2.5 font-mono text-[10px] uppercase tracking-wider"
            disabled={isFull || mutation.isPending}
            title={isFull ? `A model holds up to ${String(MAX_WINDOWS)} windows` : undefined}
            onClick={() => { setDialog({ kind: 'add' }) }}
          >
            Add window
          </Button>
        )
      }
    >
      {schedules.isError ? (
        <p className="text-sm text-destructive">Failed to load the schedule.</p>
      ) : data === undefined ? (
        <Skeleton className="h-24 w-full" />
      ) : data.schedules.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No windows yet. The model keeps its regular replica range all week.
        </p>
      ) : (
        <ScheduleTable
          schedules={data}
          canEdit={canEdit}
          isPending={mutation.isPending}
          onToggle={(entry, enabled) => {
            save(
              { kind: 'enable', id: entry.id, enabled },
              enabled ? 'Window turned on' : 'Window turned off',
            )
          }}
          onEdit={(entry) => {
            if (current !== null) setDialog({ kind: 'edit', entry, snapshot: current })
          }}
          onRemove={(entry) => {
            if (current !== null) setDialog({ kind: 'remove', entry, snapshot: current })
          }}
        />
      )}
      {data !== undefined && windows === null && !isRestricted && (
        <p className="mt-3 text-sm text-muted-foreground">
          This page cannot read one of these windows. Change the schedule on Baseten.
        </p>
      )}
      {data !== undefined && data.schedules.length > 0 && (
        <WindowUsageBlock usage={usage} timezone={data.timezone} />
      )}
      {data !== undefined && <RecentChangesBlock changes={changes} />}
      {current !== null && (dialog.kind === 'add' || dialog.kind === 'edit') && (
        <ScheduleWindowDialog
          key={dialog.kind === 'edit' ? dialog.entry.id : 'new'}
          editing={dialog.kind === 'edit' ? dialog.entry : null}
          others={
            dialog.kind === 'edit'
              ? dialog.snapshot.windows.filter((w) => w.id !== dialog.entry.id)
              : current.windows
          }
          timezone={baseFor(dialog, current).timezone}
          isPending={mutation.isPending}
          onClose={() => { setDialog(CLOSED) }}
          onSave={(saved, zone) => {
            save(
              { kind: 'save', saved, timezone: zone },
              dialog.kind === 'edit' ? 'Window updated' : 'Window added',
            )
          }}
        />
      )}
      {dialog.kind === 'remove' && (
        <ConfirmDialog
          open
          onOpenChange={(open) => { if (!open) setDialog(CLOSED) }}
          title="Delete window"
          description={`Delete ${dialog.entry.name}? The model keeps its regular replica range during those hours.`}
          confirmLabel="Delete window"
          pendingLabel="Deleting..."
          isPending={mutation.isPending}
          destructive
          onConfirm={() => {
            save({ kind: 'remove', id: dialog.entry.id }, 'Window deleted')
          }}
        />
      )}
    </ModelHomeSection>
  )
}
